import { useMemo, useState } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import {
  UserPlus, Search, ChevronLeft, Users, Stamp, FileSpreadsheet, Printer,
} from "lucide-react";

import { PERMS } from "../../../utils/perms";
import PageGate from "../../../components/PageGate";
import AccessGate from "../../../components/AccessGate";

import {
  useGetAdminApplicationsByCategoryAndDietQuery,
  useChapterEndorseAllInCategoryMutation,
  useGetDietCategoryCountsQuery,
} from "../api/probationerApplicationApi";
import {
  STATUS_TABS,
  chapterState,
  countsByTab,
  ENDORSE_LABELS,
  fmtDate,
  matchesQuery,
  matchesStatusTab,
  refereeCell,
  statusColors,
  statusLabel,
  tabLabel,
  tabStatuses,
  type EndorseState,
  type StatusKey,
} from "../utils/applicationStatus";
import {
  exportApplicationsPdf,
  exportApplicationsSpreadsheet,
} from "../utils/exportApplications";

/**
 * Entry (probationer) applications in one category of one diet.
 *
 * Deliberately the same screen as AdminUpgradeCategoryPage — entry and upgrade
 * applications are reviewed by the same committee through the same ladder, so
 * the two queues read identically. Entry applications have no upgrade route and
 * no per-application fee (the entry fee sits on the applicant's signup invoice),
 * so those two columns are absent; everything else matches.
 */

function StatusBadge({ status }: { status?: string }) {
  const s = String(status ?? "pending").toLowerCase();
  const c = statusColors(s);
  return (
    <span className={`inline-flex items-center px-2 py-1 text-xs border rounded-full ${c.pill}`} title={s}>
      {statusLabel(s)}
    </span>
  );
}

/* ── Endorsements ──────────────────────────────────────────────────────────
   The chapter and two referees each have to respond before an application can
   move. Shown on the list so the secretariat can see who to chase without
   opening every application. */

const ENDORSE_STYLES: Record<EndorseState, string> = {
  approved: "bg-emerald-50 text-emerald-700 border-emerald-100",
  rejected: "bg-rose-50 text-rose-700 border-rose-100",
  pending: "bg-amber-50 text-amber-700 border-amber-100",
  missing: "bg-gray-50 text-gray-400 border-gray-100",
  not_recorded: "bg-gray-50 text-gray-400 border-gray-100",
};

function EndorsementCell({
  name,
  state,
  on,
}: {
  name?: string | null;
  state: EndorseState;
  on?: string | null;
}) {
  return (
    <div className="min-w-[9rem]">
      <div className="text-gray-800 truncate" title={name ?? undefined}>
        {name || <span className="text-gray-300">—</span>}
      </div>
      <span
        className={`mt-0.5 inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold border rounded-full ${ENDORSE_STYLES[state]}`}
        title={on ? `on ${fmtDate(on)}` : undefined}
      >
        {ENDORSE_LABELS[state]}
      </span>
    </div>
  );
}

function CountPill({ tone, count }: { tone: StatusKey; count: number }) {
  const c = statusColors(tone);
  return <span className={`ml-2 inline-flex items-center px-2 py-0.5 text-xs border rounded-full ${c.pill}`}>{count}</span>;
}

function SummaryCard({ label, value, tone }: { label: string; value: number; tone: StatusKey }) {
  const c = statusColors(tone);
  return (
    <div className={`border rounded-xl p-3 ${c.pill}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs font-medium opacity-80">{label}</div>
    </div>
  );
}

export default function AdminProbationerApplicationDietCategoryPage() {
  const { dietId = "", categoryId = "" } = useParams();
  const location = useLocation();
  const categoryName = (location.state as any)?.categoryName as string | undefined;

  const [statusTab, setStatusTab] = useState<StatusKey>("all");
  const [q, setQ] = useState("");

  const { data, isLoading, isFetching, isError, refetch } =
    useGetAdminApplicationsByCategoryAndDietQuery(
      { dietId, categoryId: Number(categoryId) },
      { skip: !dietId || !categoryId },
    );

  const rows: any[] = useMemo(
    () => (Array.isArray((data as any)?.data) ? (data as any).data : Array.isArray(data) ? (data as any) : []),
    [data],
  );

  const title = categoryName ?? rows[0]?.category?.name ?? "Category";

  /**
   * Students need no chapter endorsement.
   *
   * A student is at an institution, not in practice, so there is no chapter
   * chairman whose signature means anything about them — the API exempts the
   * category from the rung (ProbationerApplicationWorkflow), and this screen
   * stops asking for something nobody is going to give.
   *
   * Read off the category NAME, which is what the API matches on too, rather
   * than the id in the URL: the ids are seeded and differ between databases.
   */
  const chapterExempt = String(title).trim().toLowerCase() === "student";

  /* ── TEMPORARY: endorse on the chapters' behalf ──────────────────────────
     Chapters that never log in leave a whole diet stalled at "Awaiting
     Endorsements". This records the chapter's endorsement for every such
     application at once. The server will not overwrite an endorsement a
     chapter actually gave, and only advances an application whose referees and
     fee are already in — so the button cannot skip a step, only fill this one. */
  const [endorseAll, { isLoading: endorsing }] = useChapterEndorseAllInCategoryMutation();
  const [endorseMsg, setEndorseMsg] = useState<string | null>(null);

  // Never offered on the diet that is currently running: while a diet is open,
  // chasing the chapter IS the process, and endorsing for them removes the
  // scrutiny the SOP is there to apply. The server refuses it too — this only
  // keeps the button from appearing where it would be rejected.
  const { data: dietMeta } = useGetDietCategoryCountsQuery(
    { dietId },
    { skip: !dietId },
  );
  const dietIsActive =
    !!dietMeta?.diet &&
    (!!dietMeta.diet.is_active || String(dietMeta.diet.status).toLowerCase() === "active");

  // How many this would actually touch — shown on the button so it is never a
  // blind click, and hidden entirely when there is nothing to do.
  const awaitingCount = useMemo(
    () =>
      chapterExempt
        ? 0
        : rows.filter(
            (r) =>
              tabStatuses("acknowledged").includes(String(r.status).toLowerCase()) &&
              !r.chapter_accepted_at,
          ).length,
    [rows, chapterExempt],
  );

  const handleEndorseAll = async () => {
    if (
      !window.confirm(
        `Record the chapter's endorsement on ${awaitingCount} application(s) in "${title}"?\n\n` +
          `This acts on behalf of the chapters that have not endorsed. Applications the chapter has ` +
          `already endorsed are left untouched, and any still missing referee approval or payment ` +
          `stay where they are.`,
      )
    ) {
      return;
    }
    setEndorseMsg(null);
    try {
      const res: any = await endorseAll({ dietId, categoryId: Number(categoryId) }).unwrap();
      setEndorseMsg(res?.message ?? "Chapter endorsements recorded.");
      await refetch();
    } catch (e: any) {
      setEndorseMsg(
        e?.data?.message ?? "Could not record the endorsements. Please try again.",
      );
    }
  };

  // Counted per TAB, not per status, so a row on an older name for a rung is
  // counted under the rung it belongs to rather than vanishing from every tab.
  const statusCounts = useMemo(() => countsByTab(rows), [rows]);

  const filtered = useMemo(
    () => rows.filter((r) => matchesStatusTab(r, statusTab) && matchesQuery(r, q)),
    [rows, statusTab, q],
  );

  // Exports carry what is on screen — the same rows, filed the same way — plus
  // the whole category's breakdown, so a listing always says what it is a
  // listing of.
  const exportMeta = {
    category: title,
    diet: dietMeta?.diet?.title as string | undefined,
    tab: statusTab,
    query: q,
  };

  return (
    <PageGate perm={PERMS.application_diet.allCategoriesAccess}>
      <div className="max-w-6xl mx-auto p-4 md:p-6">
        <Link
          to={`/probationer-applications/admin/submissions/diets/${dietId}/categories`}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-3"
        >
          <ChevronLeft className="w-4 h-4" /> All diet categories
        </Link>

        <div className="bg-white border rounded-xl p-6">
          {/* Header */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-indigo-50 border border-indigo-100">
                <UserPlus className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">{title} applications</h1>
                <p className="text-sm text-gray-500 mt-0.5">Review applicants entering as {title}.</p>
              </div>
            </div>
            {/* The actions in their own row, so they wrap against each other
                rather than being spaced apart by the header's justify-between
                — the same grouping the upgrade category header uses. */}
            <div className="flex flex-wrap items-center gap-2">
            {/* Both exports take the panel currently open, named in the file
                and printed on the page — an export of "everything" when you
                asked for the awaiting-endorsements list is worse than no
                export. The same pair the upgrade category list carries. */}
            <button
              onClick={() => exportApplicationsSpreadsheet(filtered, rows, exportMeta)}
              disabled={isLoading}
              title={`Download the ${tabLabel(statusTab)} listing as a spreadsheet`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-emerald-200 text-emerald-700 text-sm font-semibold hover:bg-emerald-50 disabled:opacity-50"
            >
              <FileSpreadsheet className="w-4 h-4" />
              Excel
            </button>
            <button
              onClick={() => exportApplicationsPdf(filtered, rows, exportMeta)}
              disabled={isLoading}
              title={`Print the ${tabLabel(statusTab)} listing, or save it as a PDF`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-rose-200 text-rose-700 text-sm font-semibold hover:bg-rose-50 disabled:opacity-50"
            >
              <Printer className="w-4 h-4" />
              PDF
            </button>
            {/* Assignment happens on the diet's own screen, the same way
                upgrades are handed out inside a review cycle. */}
            <Link
              to={`/probationer-applications/admin/submissions/diets/${dietId}/assignments`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-indigo-200 text-indigo-700 text-sm font-semibold hover:bg-indigo-50"
            >
              <Users className="w-4 h-4" />
              Review assignment
            </Link>
            {/* Shown on every diet that is NOT the one currently running —
                closed, assessment_ongoing, approved, pending alike — and not
                only when there is something outstanding. A button that vanishes
                when the count hits zero reads as a bug ("where did it go?"); one
                that greys out and says why is the same information without the
                mystery. The server enforces the same active-diet rule. */}
            {!dietIsActive && (
              <AccessGate perm={PERMS.application_diet?.chapterEndorseAll}>
                <button
                  onClick={handleEndorseAll}
                  disabled={endorsing || awaitingCount === 0}
                  title={
                    awaitingCount === 0
                      ? "Every application in this category already has its chapter endorsement"
                      : "Records the chapter endorsement for applications whose chapter has not acted"
                  }
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-sm font-semibold hover:bg-amber-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Stamp className="w-4 h-4" />
                  {endorsing
                    ? "Endorsing…"
                    : awaitingCount === 0
                      ? "Nothing to endorse"
                      : `Endorse ${awaitingCount} for chapters`}
                </button>
              </AccessGate>
            )}
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm"
            >
              {isFetching ? "Refreshing…" : "Refresh"}
            </button>
            </div>
          </div>

          {endorseMsg && (
            <div className="mt-4 px-3 py-2 rounded-lg border border-amber-200 bg-amber-50 text-sm text-amber-900">
              {endorseMsg}
            </div>
          )}

          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mt-5">
            {STATUS_TABS.map((t) => (
              <SummaryCard key={t.key} label={t.label} value={statusCounts.get(t.key) || 0} tone={t.key} />
            ))}
          </div>

          {/* Search */}
          <div className="mt-5 relative max-w-md">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by applicant, email, chapter…"
              className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-100"
            />
          </div>

          {/* Status tabs */}
          <div className="flex flex-wrap gap-2 mt-4">
            {STATUS_TABS.map((t) => {
              const active = statusTab === t.key;
              const c = statusColors(t.key);
              return (
                <button
                  key={t.key}
                  onClick={() => setStatusTab(t.key)}
                  className={[
                    "px-3 py-2 text-sm border rounded-md transition",
                    active ? c.active : `${c.border} ${c.inactive}`,
                  ].join(" ")}
                >
                  {t.label}
                  <CountPill tone={t.key} count={statusCounts.get(t.key) || 0} />
                </button>
              );
            })}
          </div>

          {/* Table */}
          {isLoading ? (
            <div className="mt-6 text-sm text-gray-500">Loading applications…</div>
          ) : isError ? (
            <div className="mt-6 text-sm text-red-600">Failed to load applications.</div>
          ) : filtered.length === 0 ? (
            <div className="mt-6 p-4 rounded-md border bg-gray-50 text-sm text-gray-700">
              No entry applications match your filters.
            </div>
          ) : (
            <div className="mt-5 border rounded-xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="px-4 py-2 font-medium">Applicant</th>
                    <th className="px-4 py-2 font-medium">Reviewer</th>
                    <th className="px-4 py-2 font-medium">Chapter</th>
                    <th className="px-4 py-2 font-medium">Referee 1</th>
                    <th className="px-4 py-2 font-medium">Referee 2</th>
                    <th className="px-4 py-2 font-medium">Status</th>
                    <th className="px-4 py-2 font-medium">Submitted</th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map((r) => (
                    <tr key={r.id} className="hover:bg-gray-50/60">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">
                          {[r.title, r.surname, r.other_names].filter(Boolean).join(" ") ||
                            r.user?.name ||
                            "—"}
                        </div>
                        <div className="text-xs text-gray-400">
                          {r.membership_no ?? r.user?.email ?? r.email ?? ""}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {r.reviewer
                          ? [r.reviewer.surname, r.reviewer.firstname].filter(Boolean).join(" ") ||
                            r.reviewer.membership_no
                          : <span className="text-gray-300">Unassigned</span>}
                      </td>

                      {/* Who still owes an endorsement — the reason this list is
                          opened most days. */}
                      <td className="px-4 py-3">
                        {/* A student belongs to no chapter, so an outstanding
                            endorsement here would be a wait with no end. Said
                            plainly instead of left looking overdue. */}
                        {chapterExempt ? (
                          <span className="text-xs text-gray-400">Not required</span>
                        ) : (
                          <EndorsementCell
                            name={r.chapter?.name ?? r.chapter_other}
                            state={chapterState(r)}
                            on={r.chapter_accepted_at}
                          />
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <EndorsementCell {...refereeCell(r, 0)} />
                      </td>
                      <td className="px-4 py-3">
                        <EndorsementCell {...refereeCell(r, 1)} />
                      </td>

                      <td className="px-4 py-3">
                        <StatusBadge status={r.status} />
                      </td>
                      <td className="px-4 py-3 text-gray-500">{fmtDate(r.created_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <Link
                            to={`/probationer-applications/admin/${r.id}`}
                            className="px-3 py-1.5 rounded-lg border text-xs hover:bg-gray-50"
                          >
                            Review
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </PageGate>
  );
}
